/**
 * File Upload Service
 */
import type { Jai1Config } from '../types/framework.types.js';
import { NetworkError } from '../errors/index.js';

export interface UploadFileOptions {
    content: string; // base64 encoded
    filename: string;
    filePath?: string;
    slug?: string;
    scope?: 'personal' | 'project';
    projectId?: string;
    collectionId?: number;
}

export interface FileUploadResult {
    id: number;
    url: string;
    filename: string;
    filePath: string | null;
    slug: string;
    scope: string;
    projectId: string | null;
    contentSize: number;
    contentType: string;
    createdAt: string;
    isOverride: boolean;
}

export interface ListFilesOptions {
    scope?: string;
    projectId?: string;
    limit?: number;
    offset?: number;
}

export interface ListFilesResult {
    files: Array<{
        id: number;
        filename: string;
        filePath: string | null;
        slug: string;
        url: string;
        contentSize: number;
        contentType: string;
        scope: string;
        projectId: string | null;
        createdAt: string;
        updatedAt: string;
    }>;
    total: number;
    limit: number;
    offset: number;
}

export interface FileQuotaResult {
    limitBytes: number;
    usedBytes: number;
    remainingBytes: number;
}

export class FileService {
    /**
     * Upload file (HTML or Markdown)
     */
    async upload(config: Jai1Config, options: UploadFileOptions): Promise<FileUploadResult> {
        const response = await fetch(`${config.apiUrl}/api/file/upload`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'JAI1-Access-Key': config.accessKey,
            },
            body: JSON.stringify(options),
        });

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}`;
            try {
                const error = await response.json() as any;
                errorMessage = error.message || errorMessage;
                if (error.code === 'UPLOAD_QUOTA_EXCEEDED' && error.quota) {
                    errorMessage = `${errorMessage} Used ${(error.quota.usedBytes / 1024 / 1024).toFixed(1)}MB / ${(error.quota.limitBytes / 1024 / 1024).toFixed(1)}MB.`;
                }
            } catch {
                // Ignore parse error
            }
            throw new NetworkError(errorMessage);
        }

        const data = await response.json() as any;
        return data.data;
    }

    /**
     * Get quota usage for current access key
     */
    async getQuota(config: Jai1Config): Promise<FileQuotaResult> {
        const response = await fetch(`${config.apiUrl}/api/file/quota`, {
            headers: { 'JAI1-Access-Key': config.accessKey },
        });

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}`;
            try {
                const error = await response.json() as any;
                errorMessage = error.message || errorMessage;
            } catch {
                // Ignore parse error
            }
            throw new NetworkError(errorMessage);
        }

        const data = await response.json() as any;
        return data.data;
    }

    /**
     * List uploaded files
     */
    async list(config: Jai1Config, options: ListFilesOptions = {}): Promise<ListFilesResult> {
        const params = new URLSearchParams();
        if (options.scope) params.set('scope', options.scope);
        if (options.projectId) params.set('projectId', options.projectId);
        if (options.limit) params.set('limit', options.limit.toString());
        if (options.offset) params.set('offset', options.offset.toString());

        const url = `${config.apiUrl}/api/file/list?${params}`;

        const response = await fetch(url, {
            headers: { 'JAI1-Access-Key': config.accessKey },
        });

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}`;
            try {
                const error = await response.json() as any;
                errorMessage = error.message || errorMessage;
            } catch {
                // Ignore parse error
            }
            throw new NetworkError(errorMessage);
        }

        const data = await response.json() as any;
        return data.data;
    }

    /**
     * Get file info
     */
    async get(config: Jai1Config, id: number): Promise<any> {
        const response = await fetch(`${config.apiUrl}/api/file/${id}`, {
            headers: { 'JAI1-Access-Key': config.accessKey },
        });

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}`;
            try {
                const error = await response.json() as any;
                errorMessage = error.message || errorMessage;
            } catch {
                // Ignore parse error
            }
            if (response.status === 404) throw new Error('File not found');
            throw new NetworkError(errorMessage);
        }

        const data = await response.json() as any;
        return data.data;
    }

    /**
     * Delete file
     */
    async delete(config: Jai1Config, id: number): Promise<void> {
        const response = await fetch(`${config.apiUrl}/api/file/${id}`, {
            method: 'DELETE',
            headers: { 'JAI1-Access-Key': config.accessKey },
        });

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}`;
            try {
                const error = await response.json() as any;
                errorMessage = error.message || errorMessage;
            } catch {
                // Ignore parse error
            }
            if (response.status === 404) throw new Error('File not found');
            throw new NetworkError(errorMessage);
        }
    }

    // ─── Collection methods ────────────────────────────────────────

    /**
     * Create or get existing collection (upsert by slug+scope)
     */
    async createCollection(config: Jai1Config, options: CreateCollectionOptions): Promise<CollectionResult> {
        const response = await fetch(`${config.apiUrl}/api/file/collection`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'JAI1-Access-Key': config.accessKey,
            },
            body: JSON.stringify(options),
        });

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}`;
            try {
                const error = await response.json() as any;
                errorMessage = error.message || errorMessage;
            } catch { /* ignore */ }
            throw new NetworkError(errorMessage);
        }

        const data = await response.json() as any;
        return data.data;
    }

    /**
     * Generate index page for a collection
     */
    async generateIndex(config: Jai1Config, collectionId: number): Promise<GenerateIndexResult> {
        const response = await fetch(`${config.apiUrl}/api/file/collection/${collectionId}/generate-index`, {
            method: 'POST',
            headers: { 'JAI1-Access-Key': config.accessKey },
        });

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}`;
            try {
                const error = await response.json() as any;
                errorMessage = error.message || errorMessage;
            } catch { /* ignore */ }
            throw new NetworkError(errorMessage);
        }

        const data = await response.json() as any;
        return data.data;
    }

    /**
     * Get collection detail with file list
     */
    async getCollection(config: Jai1Config, id: number): Promise<CollectionDetailResult> {
        const response = await fetch(`${config.apiUrl}/api/file/collection/${id}`, {
            headers: { 'JAI1-Access-Key': config.accessKey },
        });

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}`;
            try {
                const error = await response.json() as any;
                errorMessage = error.message || errorMessage;
            } catch { /* ignore */ }
            if (response.status === 404) throw new Error('Collection not found');
            throw new NetworkError(errorMessage);
        }

        const data = await response.json() as any;
        return data.data;
    }

    /**
     * List collections
     */
    async listCollections(config: Jai1Config, options: ListCollectionsOptions = {}): Promise<ListCollectionsResult> {
        const params = new URLSearchParams();
        if (options.scope) params.set('scope', options.scope);
        if (options.projectId) params.set('projectId', options.projectId);
        if (options.limit) params.set('limit', options.limit.toString());
        if (options.offset) params.set('offset', options.offset.toString());

        const response = await fetch(`${config.apiUrl}/api/file/collections?${params}`, {
            headers: { 'JAI1-Access-Key': config.accessKey },
        });

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}`;
            try {
                const error = await response.json() as any;
                errorMessage = error.message || errorMessage;
            } catch { /* ignore */ }
            throw new NetworkError(errorMessage);
        }

        const data = await response.json() as any;
        return data.data;
    }

    /**
     * Delete collection and all its files
     */
    async deleteCollection(config: Jai1Config, id: number): Promise<void> {
        const response = await fetch(`${config.apiUrl}/api/file/collection/${id}`, {
            method: 'DELETE',
            headers: { 'JAI1-Access-Key': config.accessKey },
        });

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}`;
            try {
                const error = await response.json() as any;
                errorMessage = error.message || errorMessage;
            } catch { /* ignore */ }
            if (response.status === 404) throw new Error('Collection not found');
            throw new NetworkError(errorMessage);
        }
    }
}

// Collection types
export interface CreateCollectionOptions {
    name: string;
    slug: string;
    scope?: 'personal' | 'project';
    projectId?: string;
}

export interface CollectionFileRecord {
    id: number;
    filename: string;
    filePath: string | null;
    url: string;
    contentSize: number;
    contentType: string;
}

export interface CollectionDetailResult extends CollectionResult {
    files: CollectionFileRecord[];
}

export interface CollectionResult {
    id: number;
    name: string;
    slug: string;
    scope: string;
    projectId: string | null;
    indexUrl: string | null;
    fileCount: number;
    totalSize: number;
    createdAt: string;
    updatedAt: string;
}

export interface GenerateIndexResult {
    indexUrl: string;
    fileCount: number;
    totalSize: number;
}

export interface ListCollectionsOptions {
    scope?: string;
    projectId?: string;
    limit?: number;
    offset?: number;
}

export interface ListCollectionsResult {
    collections: CollectionResult[];
    total: number;
    limit: number;
    offset: number;
}
